import { useCallback } from "react";
import { announce } from "@react-aria/live-announcer";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { FCI_ERROR } from "@searchui-searchforms/shared/fci";
import { useIsEditForm } from "@/widgets/classic-form/IsEditFormContext";
import { useFormAPI } from "@/widgets/classic-form/form";
import { useDefaultFormValues } from "@/widgets/classic-form/form-control/useDefaultFormValues";
import { getMetadataQueryOptions, useMetadataParamsContext } from "@/entities/form-metadata";
import type { PersonSuggestionItem } from "@/entities/tree-person";
import { useLogFCI } from "@/shared/lib/fci";
import { usePageContextQuery } from "@/shared/page-context";
import { applyPersonToForm } from "./applyPersonToForm";
import { getResetBaseFormValues } from "./getResetBaseFormValues";
import { getSuggestionLabel } from "./getSuggestionLabel";

// Re-exported from its new home to keep existing import sites/tests stable.
export { ensureNonRemovableDefaults } from "./applyPersonToForm";

export const useQuickFillSelection = () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const formApi = useFormAPI();
    const formMetadataParams = useMetadataParamsContext();
    const isEditForm = useIsEditForm();
    const defaultFormValues = useDefaultFormValues({ isEditForm });
    const { data: pageContext } = usePageContextQuery();
    const logFCI = useLogFCI();

    const onSelect = useCallback(
        async (suggestion: PersonSuggestionItem, treeId: string) => {
            try {
                const label = getSuggestionLabel(suggestion);
                const formMetadata = await queryClient.ensureQueryData(getMetadataQueryOptions(formMetadataParams));

                await applyPersonToForm({
                    personId: suggestion.PersonId,
                    treeId,
                    queryClient,
                    formApi,
                    formMetadata,
                    defaultFormValues,
                    pageContext,
                    isEditForm,
                });
                announce(t("suggestions.suggestionSelected", { label }), "polite");
            } catch (error) {
                announce(t("suggestions.suggestionSelectionFailed"), "assertive");
                logFCI({ errorType: FCI_ERROR.Convenience, errorInfo: `QuickFill selection failed: ${error}` });
            }
        },
        [
            defaultFormValues,
            formApi,
            formMetadataParams,
            pageContext.runtimeConfig.defaultYearProximity,
            isEditForm,
            queryClient,
            t,
            logFCI,
        ],
    );

    const onDeselect = useCallback(
        async (suggestion: PersonSuggestionItem) => {
            try {
                const label = getSuggestionLabel(suggestion);
                const formMetadata = await queryClient.ensureQueryData(getMetadataQueryOptions(formMetadataParams));

                announce(t("suggestions.suggestionUnselected", { label }), "polite");

                const currentValues = formApi.getValues();

                const base = getResetBaseFormValues({
                    isEditForm,
                    formMetadata,
                    defaultFormValues,
                    defaultYearProximity: pageContext.runtimeConfig.defaultYearProximity,
                });

                const resetValues = {
                    ...base,
                    name: {
                        ...base.name,
                        givenName: currentValues.name.givenName,
                        surname: currentValues.name.surname,
                        secondSurname: currentValues.name.secondSurname,
                    },
                };

                formApi.setValues(resetValues);
            } catch (error) {
                announce(t("suggestions.suggestionSelectionFailed"), "assertive");
                logFCI({ errorType: FCI_ERROR.Convenience, errorInfo: `QuickFill de-selection failed: ${error}` });
            }
        },
        [
            defaultFormValues,
            formApi,
            formMetadataParams,
            isEditForm,
            pageContext.runtimeConfig.defaultYearProximity,
            queryClient,
            t,
            logFCI,
        ],
    );

    const selectSuggestion = useCallback(
        async (suggestion: PersonSuggestionItem, treeId: string) => {
            const currentPersonId = formApi.getFieldValue("treePerson").personId;

            if (currentPersonId !== suggestion.PersonId) {
                await onSelect(suggestion, treeId);
            } else {
                await onDeselect(suggestion);
            }
        },
        [formApi, onSelect, onDeselect],
    );

    return { selectSuggestion };
};
